import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getMatchByBroadcastId, getTeams, getClubSettings, getTournament, getPlayerOfMatch } from '@/lib/supabase';
import { MATCH_CONFIG } from '@/types';

const DIVIDER = (
  <div style={{ width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,0.18)', margin: '10px 0' }} />
);

function TeamLogo({ logoUrl, name, color, size = 48 }: { logoUrl?: string | null; name: string; color: string; size?: number }) {
  const [err, setErr] = useState(false);
  if (logoUrl && !err) {
    return (
      <img
        src={logoUrl}
        alt={name}
        onError={() => setErr(true)}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'contain', background: 'rgba(255,255,255,0.08)', flexShrink: 0 }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: color,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <span style={{ color: '#fff', fontWeight: 800, fontSize: size * 0.38 }}>
        {name.charAt(0)}
      </span>
    </div>
  );
}

function SetDots({ won, max, color }: { won: number; max: number; color: string }) {
  return (
    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
      {Array.from({ length: max }).map((_, i) => (
        <div
          key={i}
          style={{
            width: 9, height: 9, borderRadius: '50%',
            background: i < won ? color : 'transparent',
            border: `2px solid ${i < won ? color : 'rgba(255,255,255,0.35)'}`,
            flexShrink: 0,
          }}
        />
      ))}
    </div>
  );
}

export default function BroadcastOverlay() {
  const { broadcastId } = useParams<{ broadcastId: string }>();
  const [matchData, setMatchData] = useState<any>(null);
  const [teams, setTeams] = useState<Map<string, any>>(new Map());
  const [allPlayers, setAllPlayers] = useState<Map<string, string>>(new Map());
  const [clubLogo, setClubLogo] = useState<string | null>(null);
  const [tournamentName, setTournamentName] = useState<string>('');
  const [loaded, setLoaded] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tournamentFetched = useRef(false);
  const [pom, setPom] = useState<{ playerName: string; teamId: string } | null>(null);
  const barRef = useRef<HTMLDivElement>(null);
  // Continuous confetti + fetch POM once when match becomes complete
  useEffect(() => {
    if (matchData?.status !== 'match-won') return;

    // Fetch POM
    if (matchData?.id) {
      getPlayerOfMatch(matchData.id)
        .then(winners => { if (winners[0]) setPom({ playerName: winners[0].playerName, teamId: winners[0].teamId }); })
        .catch(() => {});
    }

    // Fireworks container sized to the actual bar height, kept in sync via ResizeObserver
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.bottom = '0';
    container.style.left = '0';
    container.style.width = '100%';
    container.style.pointerEvents = 'none';
    container.style.zIndex = '9999';
    container.style.overflow = 'hidden';
    document.body.appendChild(container);

    const syncHeight = () => {
      const h = barRef.current ? barRef.current.offsetHeight : 90;
      container.style.height = h + 'px';
    };
    syncHeight();
    const ro = new ResizeObserver(syncHeight);
    if (barRef.current) ro.observe(barRef.current);

    let fw: any;
    import('fireworks-js').then(({ Fireworks }) => {
      fw = new Fireworks(container, {
        rocketsPoint: { min: 20, max: 80 },
        traceSpeed: 2,
        acceleration: 1.05,
        friction: 0.97,
        gravity: 1.5,
        particles: 80,
        traceLength: 2,
        explosion: 6,
        intensity: 25,
        flickering: 60,
        lineStyle: 'round',
        hue: { min: 0, max: 360 },
        delay: { min: 40, max: 80 },
        boundaries: { x: 50, y: 50, width: container.clientWidth - 100, height: container.clientHeight - 50 },
        sound: { enabled: false },
        lineWidth: { explosion: { min: 1, max: 3 }, trace: { min: 0.1, max: 1 } },
        brightness: { min: 50, max: 80 },
        decay: { min: 0.015, max: 0.03 },
        mouse: { click: false, move: false, max: 1 },
      });
      fw.start();
    });

    return () => {
      fw?.stop();
      ro.disconnect();
      container.remove();
    };
  }, [matchData?.status, matchData?.id]);

  const refreshMatch = async () => {
    if (!broadcastId) return;
    try {
      const data = await getMatchByBroadcastId(broadcastId);
      setMatchData(data);
      // Stop polling once the match is complete — no further state changes will occur
      if (data?.status === 'match-won' && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      if (data.tournament_id && !tournamentFetched.current) {
        tournamentFetched.current = true;
        try {
          const t = await getTournament(data.tournament_id);
          setTournamentName(t.name);
        } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    const init = async () => {
      if (!broadcastId) return;
      try {
        const [teamsData, settings] = await Promise.all([
          getTeams(),
          getClubSettings(),
        ]);
        setClubLogo(settings['club_logo_url'] || null);

        const teamMap = new Map<string, any>();
        const playerMap = new Map<string, string>();
        (teamsData || []).forEach((t: any) => {
          teamMap.set(t.id, t);
          (t.players || []).forEach((p: any) => playerMap.set(p.id, p.name));
        });
        setTeams(teamMap);
        setAllPlayers(playerMap);

        await refreshMatch();
      } finally {
        setLoaded(true);
      }
    };
    init();

    // Poll every 2 seconds — stops being meaningful once match is complete
    pollRef.current = setInterval(refreshMatch, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [broadcastId]);

  // Force transparent page background for streaming overlay (overrides global bg-background)
  useEffect(() => {
    const prevBody = document.body.style.cssText;
    const prevHtml = document.documentElement.style.cssText;
    document.body.style.setProperty('background', 'transparent', 'important');
    document.body.style.setProperty('background-color', 'transparent', 'important');
    document.documentElement.style.setProperty('background', 'transparent', 'important');
    document.documentElement.style.setProperty('background-color', 'transparent', 'important');
    return () => {
      document.body.style.cssText = prevBody;
      document.documentElement.style.cssText = prevHtml;
    };
  }, []);

  if (!loaded || !matchData) {
    return <div style={{ background: 'transparent', height: '100vh' }} />;
  }

  const teamA = teams.get(matchData.team_a_id);
  const teamB = teams.get(matchData.team_b_id);
  if (!teamA || !teamB) return <div style={{ background: 'transparent', height: '100vh' }} />;

  const sets: any[] = (matchData.sets || []).sort((a: any, b: any) => a.set_number - b.set_number);
  const currentSet = sets.find((s: any) => !s.ended_at);
  const completedSets = sets.filter((s: any) => s.ended_at);
  const teamASetsWon = sets.filter((s: any) => s.winner_id === matchData.team_a_id).length;
  const teamBSetsWon = sets.filter((s: any) => s.winner_id === matchData.team_b_id).length;

  const matchConfig = MATCH_CONFIG[matchData.match_type as 'league' | 'playoff'] ?? MATCH_CONFIG.playoff;
  const maxSets = matchConfig.maxSets;
  const setsToWin = matchConfig.winsNeeded;

  const currentSetScoreA = currentSet?.score_a ?? 0;
  const currentSetScoreB = currentSet?.score_b ?? 0;
  const currentSetNumber = currentSet?.set_number ?? (sets.length || 1);

  const servingTeamId = currentSet?.current_serving_team_id ?? null;
  const servingTeam = servingTeamId ? teams.get(servingTeamId) : null;
  const serverId = currentSet?.current_server_id ?? null;
  const serverName = serverId ? allPlayers.get(serverId) ?? null : null;

  const isInTimeout = matchData.status === 'timeout' ||
    (matchData.timeouts ?? []).some((t: any) => !t.ended_at);
  const activeTimeout = (matchData.timeouts ?? []).find((t: any) => !t.ended_at);
  const timeoutTeam = activeTimeout ? teams.get(activeTimeout.team_id) : null;

  const isComplete = matchData.status === 'match-won';
  const winnerId = matchData.winner_id;
  const winner = winnerId ? teams.get(winnerId) : null;
  const loserSetsWon = winnerId === matchData.team_a_id ? teamBSetsWon : teamASetsWon;
  const pomTeam = pom ? teams.get(pom.teamId) : null;
  const winnerSetsWon = winnerId === matchData.team_a_id ? teamASetsWon : teamBSetsWon;

  // Shared bar style
  const barStyle: React.CSSProperties = {
    background: 'rgba(15, 15, 15, 0.93)',
    display: 'flex',
    alignItems: 'stretch',
    width: '100%',
    minHeight: 80,
    overflow: 'hidden',
    fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
  };

  const sectionPad: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    padding: '0 18px',
    gap: 10,
  };

  // ─── LIVE OVERLAY ───────────────────────────────────────────────────────────
  if (!isComplete) {
    return (
      <div style={{ background: 'transparent', height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
        <div style={barStyle}>

          {/* Club / League Section */}
          <div style={{ ...sectionPad, flex: '0 0 auto', gap: 12 }}>
            {clubLogo ? (
              <img src={clubLogo} alt="Club" style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 6, flexShrink: 0 }} />
            ) : (
              <div style={{ width: 44, height: 44, borderRadius: 6, background: 'rgba(255,255,255,0.1)', flexShrink: 0 }} />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ color: '#fff', fontWeight: 800, fontSize: 14, letterSpacing: 0.2, whiteSpace: 'nowrap' }}>
                Starwood Premier League
              </span>
              <span style={{ color: '#9CA3AF', fontWeight: 500, fontSize: 11, whiteSpace: 'nowrap' }}>
                {tournamentName || 'Volleyball'}
              </span>
            </div>
          </div>

          {DIVIDER}

          {/* Team A Section */}
          <div style={{ ...sectionPad, gap: 12, flex: 1, justifyContent: 'flex-end' }}>
            <TeamLogo logoUrl={teamA.logo_url} name={teamA.name} color={teamA.color} size={46} />
            <span style={{ color: teamA.color, fontWeight: 800, fontSize: 20, letterSpacing: 0.5 }}>
              {teamA.short_name}
            </span>
            <span style={{ color: '#fff', fontWeight: 800, fontSize: 54, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              {currentSetScoreA}
            </span>
            <SetDots won={teamASetsWon} max={setsToWin} color={teamA.color} />
          </div>

          {/* Center — Set Info */}
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: '0 20px', gap: 4, flexShrink: 0,
          }}>
            <span style={{ color: '#9CA3AF', fontWeight: 700, fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase' }}>
              SET {currentSetNumber}/{maxSets}
            </span>
            <span style={{ color: '#22C55E', fontWeight: 800, fontSize: 18, letterSpacing: 0.5 }}>
              {teamASetsWon} &ndash; {teamBSetsWon}
            </span>
          </div>

          {/* Team B Section */}
          <div style={{ ...sectionPad, gap: 12, flex: 1, justifyContent: 'flex-start' }}>
            <SetDots won={teamBSetsWon} max={setsToWin} color={teamB.color} />
            <span style={{ color: '#fff', fontWeight: 800, fontSize: 54, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              {currentSetScoreB}
            </span>
            <span style={{ color: teamB.color, fontWeight: 800, fontSize: 20, letterSpacing: 0.5 }}>
              {teamB.short_name}
            </span>
            <TeamLogo logoUrl={teamB.logo_url} name={teamB.name} color={teamB.color} size={46} />
          </div>

          {DIVIDER}

          {/* Serving Section with Completed Set Summary + Timeouts */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 20px', gap: 16, flexShrink: 0,
          }}>
            {/* Active timeout / serving — hidden between sets */}
            {matchData.status !== 'set-won' && (isInTimeout && timeoutTeam ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                <span style={{
                  color: '#F59E0B', fontWeight: 800, fontSize: 10, letterSpacing: 1.8,
                  textTransform: 'uppercase', animation: 'pulse 1s infinite',
                }}>
                  ⏸ TIMEOUT
                </span>
                <span style={{ color: timeoutTeam.color, fontWeight: 700, fontSize: 14 }}>
                  {timeoutTeam.short_name}
                </span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                <span style={{ color: '#22C55E', fontWeight: 700, fontSize: 10, letterSpacing: 1.8, textTransform: 'uppercase' }}>
                  {servingTeam ? 'SERVING' : ''}
                </span>
                <span style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>
                  {serverName ?? (servingTeam ? 'Selecting...' : '')}
                </span>
              </div>
            ))}
            {completedSets.length > 0 && (
              <>
                <div style={{ width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,0.18)', margin: '10px 0' }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Header: team names once */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 10, fontWeight: 700 }}>
                    <span style={{ width: 18, color: 'rgba(255,255,255,0.3)' }} />
                    <span style={{ width: 32, textAlign: 'center', color: teamA.color }}>{teamA.short_name}</span>
                    <span style={{ width: 12, textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>–</span>
                    <span style={{ width: 32, textAlign: 'center', color: teamB.color }}>{teamB.short_name}</span>
                  </div>
                  {completedSets.map((s: any) => (
                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 11, fontWeight: 700 }}>
                      <span style={{ width: 18, color: 'rgba(255,255,255,0.5)' }}>S{s.set_number}</span>
                      <span style={{ width: 32, textAlign: 'center', fontVariantNumeric: 'tabular-nums', color: s.winner_id === matchData.team_a_id ? '#fff' : 'rgba(255,255,255,0.35)' }}>{s.score_a}</span>
                      <span style={{ width: 12, textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>–</span>
                      <span style={{ width: 32, textAlign: 'center', fontVariantNumeric: 'tabular-nums', color: s.winner_id === matchData.team_b_id ? '#fff' : 'rgba(255,255,255,0.35)' }}>{s.score_b}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

        </div>
      </div>
    );
  }

  // ─── MATCH COMPLETE OVERLAY ─────────────────────────────────────────────────
  return (
    <div style={{ background: 'transparent', height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <div style={barStyle} ref={barRef}>

        {/* Club / League Section */}
        <div style={{ ...sectionPad, flex: '0 0 auto', gap: 12 }}>
          {clubLogo ? (
            <img src={clubLogo} alt="Club" style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 6, flexShrink: 0 }} />
          ) : (
            <div style={{ width: 44, height: 44, borderRadius: 6, background: 'rgba(255,255,255,0.1)', flexShrink: 0 }} />
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ color: '#fff', fontWeight: 800, fontSize: 14, letterSpacing: 0.2, whiteSpace: 'nowrap' }}>
              Starwood Premier League
            </span>
            <span style={{ color: '#9CA3AF', fontWeight: 500, fontSize: 11, whiteSpace: 'nowrap' }}>
              {tournamentName || 'Volleyball'}
            </span>
          </div>
        </div>

        {DIVIDER}

        {/* Winner Section */}
        <div style={{ ...sectionPad, gap: 14, flex: 1 }}>
          <span style={{ fontSize: 28, lineHeight: 1 }}>🏆</span>
          {winner && (
            <TeamLogo logoUrl={winner.logo_url} name={winner.name} color={winner.color} size={46} />
          )}
          {winner && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ color: winner.color, fontWeight: 800, fontSize: 20, letterSpacing: 0.3, whiteSpace: 'nowrap' }}>
                {winner.name}
              </span>
              <span style={{ color: winner.color, fontWeight: 600, fontSize: 13, letterSpacing: 0.2 }}>
                {winner.short_name} won {winnerSetsWon}&ndash;{loserSetsWon}
              </span>
            </div>
          )}
        </div>

        {DIVIDER}

        {/* Set Summary Section */}
        <div style={{ ...sectionPad, gap: 10 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Header: team names once */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 10, fontWeight: 700 }}>
              <span style={{ width: 18, color: 'rgba(255,255,255,0.3)' }} />
              <span style={{ width: 36, textAlign: 'center', color: teamA.color }}>{teamA.short_name}</span>
              <span style={{ width: 12, textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>–</span>
              <span style={{ width: 36, textAlign: 'center', color: teamB.color }}>{teamB.short_name}</span>
            </div>
            {/* One row per set */}
            {completedSets.map((s: any) => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 12, fontWeight: 700 }}>
                <span style={{ width: 18, color: 'rgba(255,255,255,0.5)', fontSize: 10 }}>S{s.set_number}</span>
                <span style={{ width: 36, textAlign: 'center', fontVariantNumeric: 'tabular-nums', color: s.winner_id === matchData.team_a_id ? '#fff' : 'rgba(255,255,255,0.35)' }}>{s.score_a}</span>
                <span style={{ width: 12, textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>–</span>
                <span style={{ width: 36, textAlign: 'center', fontVariantNumeric: 'tabular-nums', color: s.winner_id === matchData.team_b_id ? '#fff' : 'rgba(255,255,255,0.35)' }}>{s.score_b}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Player of the Match */}
        {pom && pomTeam && (
          <>
            {DIVIDER}
            <div style={{ ...sectionPad, gap: 10, flexShrink: 0 }}>
              <span style={{ fontSize: 20, lineHeight: 1 }}>⭐</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <span style={{ color: '#9CA3AF', fontWeight: 700, fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                  PLAYER OF MATCH
                </span>
                <span style={{ fontWeight: 800, fontSize: 15, whiteSpace: 'nowrap' }}>
                  <span style={{ color: '#fff' }}>{pom.playerName}</span>
                  {' '}
                  <span style={{ color: pomTeam.color }}>({pomTeam.short_name})</span>
                </span>
              </div>
            </div>
          </>
        )}

      </div>
    </div>
  );
}
